<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-gray-900 bg-opacity-60 z-50 flex items-center justify-center p-4"
  >
    <!-- Loader -->
    <div
      v-if="showLoader"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
    >
      <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-500"></div>
    </div>

    <!-- Modal Body -->
    <div
      v-else
      class="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-[85vh] flex flex-col"
    >
      <!-- Header -->
      <div class="flex items-center justify-between p-5 border-b shrink-0">
        <h2 class="text-2xl font-bold text-gray-800">Cashier Queue</h2>
        <button
          @click="closeModal"
          class="text-gray-500 hover:text-gray-700 transition"
        >
          <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Main Content: Tabs + Scrollable Area -->
      <div class="flex-1 flex flex-col overflow-hidden">
        <!-- Tabs Navigation -->
        <div class="border-b bg-gray-50 px-6 shrink-0">
          <ul class="flex flex-wrap -mb-px text-sm font-medium">
            <li v-for="tab in filteredTabs" :key="tab.name" class="mr-6">
              <a
                href="#"
                @click.prevent="currentTab = tab.component"
                class="inline-block py-4 px-1 border-b-2 transition-colors duration-200"
                :class="[
                  currentTab === tab.component
                    ? 'border-custom-yellow text-custom-blue font-bold'
                    : 'border-transparent hover:text-gray-700 hover:border-gray-300'
                ]"
              >
                {{ tab.name }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Scrollable Tab Content -->
        <div class="flex-1 overflow-y-auto px-6 py-6 bg-gray-50">
          <div class="bg-white rounded-lg shadow-sm min-h-full p-6">
            <component
              :is="currentTab"
              :billDetails="billDetails"
              :paymentMethods="paymentMethods"
              @navigateToReceiptInfo="currentTab = 'ReceiptInfo'"
              @refetch="getBillDetails"
            />
          </div>
        </div>
      </div>

      <!-- Footer Buttons -->
      <div class="p-5 border-t bg-gray-50 shrink-0 flex justify-end gap-3">
        <button
          v-if="currentTab === 'InvoiceTab'"
          @click="moveToNextTab"
          class="px-8 py-2 bg-custom-yellow hover:bg-yellow-600 text-white font-medium rounded shadow transition"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";
import InvoiceTab from "@/views/tabs/cashier_tabs/InvoiceTab.vue";
import PaymentInformation from "@/views/tabs/cashier_tabs/PaymentInformation.vue";
import ReceiptInfo from "@/views/tabs/cashier_tabs/ReceiptInfo.vue";

export default {
  name: "CashierQueueModal",
  components: {
    InvoiceTab,
    PaymentInformation,
    ReceiptInfo,
  },
  props: {
    show: Boolean,
    invoiceDetails: Object,
    billId: Number,
  },
  data() {
    return {
      tabs: [
        { name: "Invoice", component: "InvoiceTab" },
        { name: "Payment Info", component: "PaymentInformation" },
        { name: "Receipt Info", component: "ReceiptInfo" },
      ],
      currentTab: "InvoiceTab",
      billDetails: {},
      showLoader: false,
      paymentMethods: [],
    };
  },
  computed: {
    filteredTabs() {
      if (this.billDetails.status?.code === "ST-014") {
        return this.tabs.filter(t => t.component !== "PaymentInformation");
      } else if (this.billDetails.status?.code === "ST-006") {
        return this.tabs.filter(t => t.component !== "ReceiptInfo");
      }
      return this.tabs;
    },
  },
  watch: {
    billId(newVal) {
      if (newVal) this.getBillDetails();
    },
    show(newVal) {
      if (newVal && this.billId) this.getBillDetails();
    },
  },
  mounted() {
    if (this.billId) this.getBillDetails();
    this.getPaymentMethods();
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    moveToNextTab() {
      const currentIndex = this.tabs.findIndex(t => t.component === this.currentTab);
      if (currentIndex < this.tabs.length - 1) {
        const nextIndex = this.billDetails.status?.code === "ST-014" ? currentIndex + 2 : currentIndex + 1;
        if (nextIndex < this.tabs.length) {
          this.currentTab = this.tabs[nextIndex].component;
        }
      }
    },
    async getBillDetails() {
      if (!this.billId) return;
      try {
        this.showLoader = true;
        const response = await apiClient.getBill(this.billId);
        if (response.success) {
          this.billDetails = response.data;
        } else {
          this.$toast.error("Failed to load bill");
        }
      } catch (err) {
        this.$toast.error("Network error");
        console.error(err);
      } finally {
        this.showLoader = false;
      }
    },
    async getPaymentMethods() {
      try {
        const response = await apiClient.getPaymentMethods();
        if (response.success) {
          this.paymentMethods = response.data;
        } else {
          this.showLoader = false;
          this.$toast.error("Failed to fetch payment methods");
        }
      } catch (error) {
        this.showLoader = false;
        this.$toast.error("Failed to fetch payment methods");
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
/* Ensure long receipts in print preview don't break layout */
@media print {
  .print\\:hidden { display: none !important; }
}
</style>